<template>
<space-map
  ref="space-map"
  :center="system.center"
  :default-zoom="defaultZoom"
  :boundaries="boundaries"
  :geo-json="geoJson"
  :geo-json-style="geoJsonStyle"
  :sites="formatFilterSites"
>
  <template #toolbar>
    <period-time
      :query="query"
      :start-date-picker-options="startDatePickerOptions"
      :end-date-picker-options="endDatePickerOptions"
      :show-options="granularityValues"
      style="display:flex;flex-wrap:wrap;align-items:center;gap:6px;"
    >
      <el-select v-model="displayFactorId" size="small" style="width:100px;">
        <el-option
          v-for="factorOption in factorOptions"
          :key="factorOption.value"
          :value="factorOption.value"
          :label="factorOption.label"
        />
      </el-select>

      <el-radio-group v-model="displayValueKey" :disabled="displayFactorId==='aqi'" size="small">
        <el-radio-button label="-iaqi">IAQI</el-radio-button>
        <el-radio-button label="-value">浓度</el-radio-button>
      </el-radio-group>

      <completed-query v-model="query.isAuditCompleted" @change="onAuditCompletedChanged" />
      <!-- <el-checkbox v-model="query.isMultipleIntegral" size="small" label="重积分" border v-if="query.isAuditCompleted !== false" /> -->

      <el-button
        :disabled="canNotSearch"
        type="orange"
        size="small"
        icon="el-icon-search"
        @click="onSearchBtnClick"
      >查询</el-button>

      <el-button
        :disabled="dataTimes.length === 0"
        type="success"
        size="small"
        @click="onRecordScreenBtnClick"
      >导出gif</el-button>
    </period-time>
  </template>

  <template #map-ele>
    <div v-if="geoJson" class="legend-wrapper">
      <div class="legend-section" :style="{
        background: `linear-gradient(${legendColors})`
      }"></div>
      <div class="legend-labels">
        <label>{{Number(levels.levels[0].toFixed(3))}}{{currentFactorOption.unit}}</label>
        <label>{{Number(levels.levels[levels.levels.length - 1].toFixed(3))}}{{currentFactorOption.unit}}</label>
      </div>
    </div>
  </template>

  <data-player ref="data-player" v-model="dataIndex" :options="dataTimes" style="position:absolute;bottom:0;left:12px;" />

  <data-time-switch v-if="reverseDataTimes.length > 0" v-model="dataIndex" :options="reverseDataTimes" />
</space-map>
</template>

<script>
import BaseContourSurface from '../../../BaseContourSurface'
import DeviceCategoryConfig from '../../../../model/DeviceCategoryConfig'

import SpaceMap from '../../../../components/SpaceMap/index'
import PeriodTime from '../../../../components/Data/PeriodTime'
import DataPlayer from '../../../../components/DataPlayer/index'
import DataTimeSwitch from '../../../../components/DataTimeSwitch/index'

export default {
  name: 'AQISpace',
  routeInfo: {
    label: 'AQI空间分布',
    icon: 'fa fa-users'
  },
  extends: BaseContourSurface,
  components: {SpaceMap, PeriodTime, DataPlayer, DataTimeSwitch},
  data: () => ({
    query: {
      granularity: 'hour'
    },
    displayFactorId: 'aqi',
    displayValueKey: '-iaqi'
  }),
  computed: {
    siteDeviceCategoryConfig: () => [
      new DeviceCategoryConfig(1, true),
      new DeviceCategoryConfig(process.env.SO2_DEVICE_CATEGORY, false, siteDeviceFactors => siteDeviceFactors.filter(siteDeviceFactor => siteDeviceFactor.factorId === '1001')),
      new DeviceCategoryConfig(process.env.NO_DEVICE_CATEGORY, false, siteDeviceFactors => siteDeviceFactors.filter(siteDeviceFactor => siteDeviceFactor.factorId === '1003')),
      new DeviceCategoryConfig(process.env.CO_DEVICE_CATEGORY, false, siteDeviceFactors => siteDeviceFactors.filter(siteDeviceFactor => siteDeviceFactor.factorId === '1005')),
      new DeviceCategoryConfig(process.env.O3_DEVICE_CATEGORY, false, siteDeviceFactors => siteDeviceFactors.reduce((list, siteDeviceFactor) => {
        if (siteDeviceFactor.factorId === '1006') {
          list.push(siteDeviceFactor, {
            ...siteDeviceFactor,
            factorId: '1006-8H'
          })
        }

        return list
      }, [])),
      new DeviceCategoryConfig(process.env.PM_DEVICE_CATEGORY, false, siteDeviceFactors => siteDeviceFactors.reduce((list, siteDeviceFactor) => {
        const match = siteDeviceFactor.factorId.match(/^100(7|8)$/)

        if (match) {
          list.push(siteDeviceFactor, {
            ...siteDeviceFactor,
            factorId: `${match[0]}-24H`
          })
        }

        return list
      }, []))
    ],
    minSiteSelectNumber () {
      return this.filterSites.length
    },
    factorOptions () {
      const {displayValueKey} = this

      const factorOptions = [{
        value: 'aqi',
        label: 'AQI',
        unit: ''
      }, {
        value: '1001',
        label: 'SO2',
        unit: displayValueKey === '-value' ? 'μg/m3' : ''
      }, {
        value: '1003',
        label: 'NO2',
        unit: displayValueKey === '-value' ? 'μg/m3' : ''
      }, {
        value: '1005',
        label: 'CO',
        unit: displayValueKey === '-value' ? 'mg/m3' : ''
      }]

      if (this.currentQuery.granularity === 1) {
        factorOptions.push({
          value: '1006',
          label: 'O3',
          unit: displayValueKey === '-value' ? 'μg/m3' : '',
          rootFactorId: '1006-8H'
        })
      } else {
        factorOptions.push({
          value: '1006-8H',
          label: 'O3',
          unit: displayValueKey === '-value' ? 'μg/m3' : '',
          rootFactorId: '1006'
        })
      }

      factorOptions.push({
        value: '1007',
        label: 'PM10',
        unit: displayValueKey === '-value' ? 'μg/m3' : '',
        rootFactorId: '1007'
      }, {
        value: '1008',
        label: 'PM2.5',
        unit: displayValueKey === '-value' ? 'μg/m3' : '',
        rootFactorId: '1008'
      })

      return factorOptions
    },
    currentFactorOption () {
      const {displayFactorId} = this
      return this.factorOptions.find(factorOption => factorOption.value === displayFactorId)
    },
    gifFilename () {
      const {currentQuery, currentFactorOption, displayValueKey} = this
      const {complete, displayFormatter} = this.currentGranularity
      return `${currentFactorOption.label.replace(/\./g, '')}${this.displayFactorId === 'aqi' ? '' : (displayValueKey === 'value' ? '浓度' : 'IAQI')}_${new Date(`${currentQuery.beginTime + complete}`).format(displayFormatter)}-${new Date(`${currentQuery.endTime + complete}`).format(displayFormatter)}.gif`
    },
    startDatePickerOptions () {
      return {
        disabledDate: time => time.getTime() > new Date(this.query.endTime)
      }
    },
    endDatePickerOptions () {
      return {
        disabledDate: time => time.getTime() < new Date(this.query.beginTime)
      }
    },
    AQI_LEVEL_MAP () {
      const AQI_LEVEL_MAP = {
        aqi: [0, 50, 100, 150, 200, 300, 500],
        '1001-iaqi': [0, 50, 100, 150, 200, 300, 500],
        '1003-iaqi': [0, 50, 100, 150, 200, 300, 500],
        '1005-iaqi': [0, 50, 100, 150, 200, 300, 500],
        '1006-iaqi': [0, 50, 100, 150, 200, 300, 500],
        '1006-8H-iaqi': [0, 50, 100, 150, 200, 300, 500],
        '1007-24H-iaqi': [0, 50, 100, 150, 200, 300, 500],
        '1008-24H-iaqi': [0, 50, 100, 150, 200, 300, 500],
        '1007-24H-value': [0, 50, 150, 250, 350, 420, 600],
        '1008-24H-value': [0, 35, 75, 115, 150, 250, 500],
        '1007-iaqi': [0, 50, 100, 150, 200, 300, 500],
        '1008-iaqi': [0, 50, 100, 150, 200, 300, 500],
        '1007-value': [0, 50, 150, 250, 350, 420, 600],
        '1008-value': [0, 35, 75, 115, 150, 250, 500]
      }

      if (this.currentQuery.granularity === 1) {
        AQI_LEVEL_MAP['1001-value'] = [0, 150, 500, 650, 800, 1600, 2620]
        AQI_LEVEL_MAP['1003-value'] = [0, 100, 200, 700, 1200, 2340, 3840]
        AQI_LEVEL_MAP['1005-value'] = [0, 5, 10, 35, 60, 90, 150]
        AQI_LEVEL_MAP['1006-value'] = [0, 160, 200, 300, 400, 800, 1200]
      } else {
        AQI_LEVEL_MAP['1001-value'] = [0, 50, 150, 475, 800, 1600, 2620]
        AQI_LEVEL_MAP['1003-value'] = [0, 40, 80, 180, 280, 565, 940]
        AQI_LEVEL_MAP['1005-value'] = [0, 2, 4, 14, 24, 36, 60]
        AQI_LEVEL_MAP['1006-8H-value'] = [0, 100, 160, 215, 265, 800, 1200]
      }

      return AQI_LEVEL_MAP
    },
    valueKey () {
      const {displayFactorId} = this
      return `${displayFactorId}${displayFactorId === 'aqi' ? '' : this.displayValueKey}`
    }
  },
  methods: {
    setCurrentQuery () {
      const {query} = this

      this.currentQuery = Object.freeze({
        beginTime: query.beginTime,
        endTime: query.endTime,
        dataType: 60,
        siteIds: query.siteIds,
        factorIds: query.factorIds,
        granularity: this.granularityMap[this.query.granularity].id,
        isAuditCompleted: query.isAuditCompleted,
        isFuse: query.isFuse,
        isSiteMerge: false,
        isTimeMerge: false
      })

      const {displayFactorId} = this
      const factorOption = this.factorOptions.find(factorOption => factorOption.value === displayFactorId || factorOption.rootFactorId === displayFactorId)
      this.displayFactorId = factorOption.value
    },
    getData () {
      const {currentQuery} = this
      const {siteIds} = currentQuery

      if (siteIds.length === 0) {
        this.dataTimes = Object.freeze([])
        this.dataTime = null
        this.dataMap = null
        return
      }

      this.clearPlayTimer()
      this.changeLoadStatus(true)

      this.$api.Aqi.getAQIData(currentQuery).then(this.onAQIDataResponse).catch(console.error).finally(() => {
        this.changeLoadStatus(false)
      })
    },
    onAQIDataResponse (aqiHourlies) {
      this.formatAQIData(aqiHourlies)
    }
  }
}
</script>

<style lang="scss" scoped>
.legend-wrapper {
  position: absolute;
  display: grid;
  grid-template-columns: 30px max-content;
  grid-column-gap: 5px;
  bottom: 100px;
  left: 10px;
  height: 300px;
}

.legend-labels {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  line-height: 0;
  color: #303133;
}

.data-time-switch {
  position: absolute;
  top: calc(.8em + 58px);
  right: 12px;
  bottom: 70px;
  min-width: 170px;
  width: max-content;
  background-color: rgba(255,255,255,0.3);

  /deep/ {
    > .data-time-switch-header {
      background-color: rgba(255, 255, 255, .3);
    }

    > .data-time-switch-list {
      > li {
        &:hover {
          background-color: rgba(64, 148, 255, .1);
        }

        &.active {
          color: #ffffff;
          background-color: rgba(64, 148, 255, .3);
          cursor: default;
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/04Statistical-Analysis/03Pollution/10AQI-Space/index.vue